import React from "react";
import { Link } from "react-router-dom";
import { LEGAL_PATHS } from "../../legal/legalPaths";
import { TREYDORA_DISCORD_INVITE_URL } from "../../data/supportDashboardMockData";
import { SUPPORT_NFT_COMIC_MERCH_FLOW } from "../../constants/supportPublicFlowRoutes";
import {
  SITE_DASHBOARD_PATH,
  SITE_DASHBOARD_SUPPORT_PATH,
  SITE_MARKETPLACE_PATH,
  SITE_MY_LIBRARY_PATH,
  SITE_OSHAR_REAL_ESTATE_PATH,
  SITE_PUBLIC_LIBRARY_PATH,
} from "../../constants/siteNavRoutes";
import { SUPPORT_SEO } from "../../constants/siteSeo";
import { usePageSeo } from "../../hooks/usePageSeo";
import { trackSupportClick } from "../../utils/analytics";
import "../Legal/legal-pages.css";
import "./support-public.css";

const MAIL_SUPPORT = "mailto:info@treydora.com?subject=Treydora%20Support";

const TOPICS = [
  {
    key: "account",
    title: "Account & purchase help",
    body: "Sign-in, Gateway access, Stripe receipts, and purchase status. For ticket threads and AI-assisted triage, use your signed-in Support Command Center.",
    links: [
      {
        label: "Open Support Command Center",
        to: SITE_DASHBOARD_SUPPORT_PATH,
        primary: true,
        hint: "Signed-in tickets and AI-assisted triage",
      },
      { label: "Gateway dashboard", to: SITE_DASHBOARD_PATH, hint: "Account hub, quests, and receipts" },
      { label: "Refund policy", to: LEGAL_PATHS.refundPolicy, hint: "Eligibility and timelines" },
    ],
  },
  {
    key: "comic",
    title: "NFT Comic & Merch Rights",
    body: "Review Genesis Comic holder rights, merch usage boundaries, public comic access, and your owned library items.",
    links: [
      {
        label: "Read Merchandiser Terms",
        to: SUPPORT_NFT_COMIC_MERCH_FLOW.merchandiserTerms,
        primary: true,
        hint: "Legal rights and usage boundaries",
      },
      {
        label: "View Genesis Comic",
        to: SUPPORT_NFT_COMIC_MERCH_FLOW.publicLibrary,
        hint: "Browse public comic and book assets",
      },
      {
        label: "Open My Library",
        to: SUPPORT_NFT_COMIC_MERCH_FLOW.myLibrary,
        hint: "Access items you own",
      },
    ],
  },
  {
    key: "re",
    title: "VR real estate & storefront",
    body: "Oshar reservations, Stripe checkout for parcels, and storefront preview eligibility. Legal terms describe NFT property rights and marketplace seller obligations.",
    links: [
      { label: "Oshar VR real estate", to: SITE_OSHAR_REAL_ESTATE_PATH, hint: "Reserve parcels and checkout" },
      { label: "VR Real Estate NFT terms", to: LEGAL_PATHS.vrRealEstateTerms, hint: "Property NFT rights" },
      { label: "Marketplace hub", to: SITE_MARKETPLACE_PATH, hint: "Browse stores and products" },
      { label: "Marketplace seller terms", to: LEGAL_PATHS.marketplaceSellerTerms, hint: "Seller obligations" },
    ],
  },
  {
    key: "wallet",
    title: "Wallet & MetaMask",
    body: "Use the same wallet you used at checkout, confirm the correct network in MetaMask, then retry sync from Gateway or your Collector Vault.",
    links: [
      { label: "Open My Library", to: SITE_MY_LIBRARY_PATH, primary: true, hint: "Collector Vault sync and owned items" },
      { label: "Digital asset risk disclosure", to: LEGAL_PATHS.digitalAssetRisk, hint: "Volatility and custody risks" },
    ],
  },
  {
    key: "contact",
    title: "General contact",
    body: "No anonymous ticket form on this page — email is the reliable public channel. Community questions can also go through official social channels in the site footer.",
    links: [
      { label: "Email info@treydora.com", href: MAIL_SUPPORT, hint: "Direct message to the team" },
      ...(TREYDORA_DISCORD_INVITE_URL
        ? [
            {
              label: "Treydora Discord (community)",
              href: TREYDORA_DISCORD_INVITE_URL,
              hint: "Community discussion (not official support)",
            },
          ]
        : []),
      { label: "Legal center", to: LEGAL_PATHS.center, hint: "All published policies" },
    ],
  },
];

export default function PublicSupportPage() {
  usePageSeo(SUPPORT_SEO);

  const trackSupportCta = (topicKey, index, link) =>
    trackSupportClick({
      cta_id: `support_${topicKey}_${index}`,
      section: "public_support",
      destination_type: link.href ? (String(link.href).startsWith("mailto:") ? "email" : "external") : "internal",
      primary: Boolean(link.primary),
    });

  return (
    <div className="td-legal td-legal--center td-support-public">
      <section className="td-legal-hero" aria-labelledby="td-support-hero-title">
        <div className="td-legal-hero__bg" aria-hidden />
        <div className="td-legal-hero__grid" aria-hidden />
        <div className="td-legal-hero__vignette" aria-hidden />
        <div className="td-legal-hero__inner">
          <p className="td-legal-hero__eyebrow">Treydora · public signal desk</p>
          <h1 id="td-support-hero-title" className="td-legal-hero__title">
            Support &amp; help paths
          </h1>
          <p className="td-legal-hero__lead">
            Choose a lane below for purchases, NFTs, Oshar real estate, wallets, and legal references. Signed-in players can open the{" "}
            <Link
              className="td-legal-inline-link"
              to={SITE_DASHBOARD_SUPPORT_PATH}
              onClick={() =>
                trackSupportClick({
                  cta_id: "support_hero_command_center",
                  section: "public_support_hero",
                  destination_type: "internal",
                })
              }
            >
              Support Command Center
            </Link>{" "}
            for tickets and in-dashboard assistance — this page stays public for everyone.
          </p>
          <p className="td-legal-hero__meta">
            <span className="td-legal-pill">Public</span>
            <span className="td-legal-hero__date">No admin tools · no fake forms</span>
          </p>
        </div>
      </section>

      <div className="td-legal-inner">
        <div className="td-support-grid">
          {TOPICS.map((t) => (
            <article key={t.key} className="td-support-card">
              <div className="td-support-card__shine" aria-hidden />
              <h2 className="td-support-card__title">{t.title}</h2>
              <p className="td-support-card__body">{t.body}</p>
              <ul className="td-support-card__links">
                {t.links.map((l, index) => (
                  <li key={`${t.key}-${l.label}`}>
                    {l.href ? (
                      <a
                        className={`td-support-card__cta${l.primary ? " td-support-card__cta--primary" : ""}`}
                        href={l.href}
                        onClick={() => trackSupportCta(t.key, index, l)}
                        {...(String(l.href).startsWith("mailto:")
                          ? {}
                          : { target: "_blank", rel: "noreferrer noopener" })}
                      >
                        {l.label}
                      </a>
                    ) : (
                      <Link
                        className={`td-support-card__cta${l.primary ? " td-support-card__cta--primary" : ""}`}
                        to={l.to}
                        onClick={() => trackSupportCta(t.key, index, l)}
                      >
                        {l.label}
                      </Link>
                    )}
                    {l.hint ? <p className="td-support-card__cta-hint">{l.hint}</p> : null}
                  </li>
                ))}
              </ul>
            </article>
          ))}
        </div>

        <footer className="td-support-foot">
          <p>
            Prefer the vault?{" "}
            <Link className="td-legal-inline-link" to={SITE_PUBLIC_LIBRARY_PATH}>
              Browse the public library
            </Link>{" "}
            or{" "}
            <Link className="td-legal-inline-link" to={SITE_MY_LIBRARY_PATH}>
              open your Collector Vault
            </Link>{" "}
            when signed in.
          </p>
        </footer>
      </div>
    </div>
  );
}
